import { useState, useEffect, useCallback } from 'react';

/**
 * Hook per gestire filtri salvabili in localStorage
 * @param {string} key - Chiave univoca per salvare i filtri (es. 'volontari-filters')
 * @param {object} defaultFilters - Filtri di default
 */
export function useSavedFilters(key, defaultFilters = {}) {
  const storageKey = `assieticagest_filters_${key}`;
  
  // Load saved filters on mount
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        return { ...defaultFilters, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
    return defaultFilters;
  });

  // Save filters to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (e) {
      console.error('Error saving filters:', e);
    }
  }, [filters, storageKey]);

  const updateFilter = useCallback((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
    localStorage.removeItem(storageKey);
  }, [defaultFilters, storageKey]);

  const hasActiveFilters = Object.keys(filters).some(
    key => filters[key] !== defaultFilters[key] && filters[key] !== '' && filters[key] !== 'all'
  );

  return {
    filters,
    setFilters,
    updateFilter,
    resetFilters,
    hasActiveFilters
  };
}

/**
 * Hook per gestire la configurazione della dashboard personalizzabile
 */
export function useDashboardConfig() {
  const storageKey = 'assieticagest_dashboard_config';
  
  const defaultConfig = {
    widgets: [
      { id: 'plan', enabled: true, order: 0 },
      { id: 'stats', enabled: true, order: 1 },
      { id: 'alerts', enabled: true, order: 2 },
      { id: 'recent-events', enabled: true, order: 3 },
      { id: 'quick-actions', enabled: true, order: 4 },
      { id: 'recent-activities', enabled: true, order: 5 },
      { id: 'soci-stats', enabled: true, order: 6 },
    ],
    compactMode: false
  };

  const [config, setConfig] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults to ensure all widgets are present
        const mergedWidgets = defaultConfig.widgets.map(defaultWidget => {
          const savedWidget = parsed.widgets?.find(w => w.id === defaultWidget.id);
          return savedWidget || defaultWidget;
        });
        return { ...defaultConfig, ...parsed, widgets: mergedWidgets };
      }
    } catch (e) {
      console.error('Error loading dashboard config:', e);
    }
    return defaultConfig;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(config));
    } catch (e) {
      console.error('Error saving dashboard config:', e);
    }
  }, [config]);

  const toggleWidget = useCallback((widgetId) => {
    setConfig(prev => ({
      ...prev,
      widgets: prev.widgets.map(w =>
        w.id === widgetId ? { ...w, enabled: !w.enabled } : w
      )
    }));
  }, []);

  const reorderWidgets = useCallback((sourceIndex, destIndex) => {
    setConfig(prev => {
      const newWidgets = [...prev.widgets];
      const [removed] = newWidgets.splice(sourceIndex, 1);
      newWidgets.splice(destIndex, 0, removed);
      return {
        ...prev,
        widgets: newWidgets.map((w, i) => ({ ...w, order: i }))
      };
    });
  }, []);

  const resetConfig = useCallback(() => {
    setConfig(defaultConfig);
    localStorage.removeItem(storageKey);
  }, []);

  const enabledWidgets = config.widgets
    .filter(w => w.enabled)
    .sort((a, b) => a.order - b.order);

  return {
    config,
    setConfig,
    toggleWidget,
    reorderWidgets,
    resetConfig,
    enabledWidgets
  };
}

export default { useSavedFilters, useDashboardConfig };
